/* eslint-disable no-underscore-dangle */
import React from 'react';
import { Link } from 'react-router';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faChevronDown,
  faChevronUp,
  faCirclePlus,
} from '@fortawesome/pro-regular-svg-icons';
import get from 'lodash/get';
import isString from 'lodash/isString';
import {
  Button,
  Dot,
  Link as ThemeLink,
  Modal,
  TableCellProps,
  TableDatum,
  Text,
  ColorStyleProps,
  parseChartColors,
  parseColor,
  Badge,
  BadgeProps,
  ExpandedRowProps,
  useTheme,
  getIcon,
  ActionsButton,
  Cell,
  IconProps,
} from '@quality24/design-system';

import { IconProp } from '@fortawesome/fontawesome-svg-core';
import classNames from 'classnames';
import {
  CustomComponentMap,
  Customizations,
  TableCellRender,
  TableGroupByInterface,
} from '../../../../@types';

import { isMappedLabel, parseDataValue } from '../../../../utils/dataUtils';
import { aggregateFooter } from '../../utils/aggregation';
import { convertTime, TimeUnit } from '../../../../utils/momentUtils';

import styles from './TableContainer.module.scss';
import { ComparisonData } from './types';

export interface Props extends TableCellProps<TableDatum> {
  config: TableCellRender;
  formattedValue: string | number;
  group: TableGroupByInterface;
  customCellComponents?: Customizations['customCellComponents'];
}

export interface CustomCellProps extends React.HtmlHTMLAttributes<Element> {
  cell: TableCellProps<TableDatum>['cell'];
  row?: TableCellProps<TableDatum>['row'];
  formattedValue?: string | number;
  icon?: string;
  label?: string;
  map?: CustomComponentMap;
}

export interface CustomActionCellProps
  extends React.HtmlHTMLAttributes<Element> {
  cell: Cell<TableDatum, unknown>;
  formattedValue?: string | number;
  icon?: IconProps['icon'];
  label?: string;
  value?: string;
  onTrigger?: () => void;
}

/**
 * Parses the link using the original data
 * @param data
 * @param link
 * @returns
 */
const parseLink = (data: Record<string, string | number>, link: string) => {
  const variables = [...link.matchAll(/{{(\$[\w\d.[\]]+)?}}/g)].map(
    (match) => ({
      key: match[1],
      value: parseDataValue(data, match[1], 'string'),
    }),
  );
  return variables.reduce(
    (acc, variable) =>
      acc.replace(`{{${variable.key}}}`, String(variable.value)),
    link,
  );
};

/**
 * <CustomCell> page
 */
export const CustomCell: React.FunctionComponent<Props> = ({
  config: {
    link,
    textEllipsize,
    legendDot,
    compareTo,
    expandable,
    expandableIcon,
    badge,
    actions,
    custom,
  },
  group: { key: groupKey, aggregation, cellFormatter, mapping },
  formattedValue,
  value,
  cell,
  row,
  rows,
  customCellComponents,
}) => {
  const [show, setShow] = React.useState(false);
  const { theme } = useTheme();

  // Parse the colors to a legendDot
  const colorArray = React.useMemo(() => {
    if (!legendDot || legendDot === 'default') {
      return parseChartColors(theme.color.chart as ColorStyleProps[]);
    }
    return parseChartColors(legendDot as ColorStyleProps[]);
  }, [legendDot, theme.color.chart]);

  // Parse link variables
  const parsedLink = React.useMemo(() => {
    if (!link?.to) return '';

    try {
      const data = JSON.parse((cell.row.original._original as string) || '');
      return parseLink(data, link?.to);
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error('Could not parse patient link', link.to);
      return link.to;
    }
  }, [link, cell.row.original]);

  // Handle the compareTo case
  const comparisonData = React.useMemo<ComparisonData | null>(() => {
    if (!compareTo || !aggregation) return null;

    let comparison = value || NaN;
    if (compareTo.type === 'total') {
      comparison -= aggregateFooter(
        rows.map((r) => r.values),
        groupKey,
        aggregation,
      );
    } else if (compareTo.type === 'ref') {
      const comparisonRef = get(
        cell.row.original,
        `${groupKey}-${compareTo.key}`,
        null,
      ) as number;
      if (comparisonRef === 0 || comparisonRef === null) {
        return { color: 'inherit', value: null, prefix: '' };
      }

      // Convert unit if cell is a duration, or else just subtract both values
      if (cellFormatter?.type === 'duration') {
        const cValue = convertTime(comparison, cellFormatter?.unit as TimeUnit);
        comparison = cValue - convertTime(comparisonRef, compareTo.unit);
      } else {
        comparison = value - comparisonRef;
      }
    } else if (compareTo.type === 'zero') {
      comparison = value;
      if (comparison === 0) {
        return { color: 'inherit', value: comparison, prefix: '' };
      }
    }

    // Convert to color
    if (Number.isNaN(comparison)) return null;

    // The body of the cell can collapse into a `mapping` label while the raw
    // value is still non-zero — a sub-second difference formatted as `00:00:00`
    // and mapped to `-`, for instance. The 'signs' renderer glues the prefix
    // onto that body, producing text like `+-`, so present it the same way as
    // the no-difference case above: no sign, no highlight. The 'dot' renderer
    // keeps its color, since it shows direction beside the value rather than
    // concatenated to it.
    if (compareTo.as === 'signs' && isMappedLabel(formattedValue, mapping)) {
      return { color: 'inherit', value: comparison, prefix: '' };
    }

    if (comparison > 0) {
      return {
        backgroundColor: 'red100_28',
        color: 'red500',
        value: comparison,
        prefix: '+',
      };
    }
    if (comparison < 0) {
      return {
        backgroundColor: 'green100_28',
        color: 'green500',
        value: comparison,
        prefix: '',
      };
    }
    return {
      backgroundColor: 'yellow100_28',
      color: 'yellow500',
      value: comparison,
      prefix: '',
    };
  }, [
    compareTo,
    aggregation,
    groupKey,
    value,
    rows,
    cell,
    cellFormatter,
    formattedValue,
    mapping,
  ]);

  return (
    <div className={styles.wrapper}>
      {/* Link wrapper */}
      {parsedLink && (
        <ThemeLink as={Link} to={parsedLink}>
          {formattedValue}
        </ThemeLink>
      )}

      {/* Ellipsized text */}
      {textEllipsize && (
        <>
          <Text className="ellipsize" size="sm2">
            {formattedValue}
          </Text>
          <Button
            className={styles.button}
            size="sm"
            variant="link"
            onClick={() => setShow(true)}
          >
            <FontAwesomeIcon icon={faCirclePlus} />
          </Button>

          <Modal
            id="description-modal"
            title={
              isString(cell.column.Header) ? cell.column.Header : 'Descrição'
            }
            show={show}
            onHide={() => setShow(false)}
          >
            <Text>{formattedValue}</Text>
          </Modal>
        </>
      )}

      {/* Chart legend dot */}
      {legendDot && (
        <>
          <Dot color={colorArray[cell.row.index]} />
          {formattedValue}
        </>
      )}

      {/* Comparison with dot */}
      {compareTo && compareTo.as !== 'signs' && (
        <>
          <Text className="flex-grow-1" size="sm2" weight="bold" as="span">
            {formattedValue}
          </Text>
          {comparisonData?.color && (
            <Dot color={parseColor(comparisonData.color)} />
          )}
        </>
      )}

      {/* Comparison with signs */}
      {compareTo && compareTo.as === 'signs' && (
        <div
          className={classNames(
            'd-flex flex-items-center flex-justify-center p-2 rounded-md',
            `bg-${comparisonData?.backgroundColor}`,
          )}
        >
          <Text
            as="span"
            size="sm2"
            weight="bold"
            color={comparisonData?.color}
          >
            {`${comparisonData?.prefix ?? ''}${formattedValue}`}
          </Text>
        </div>
      )}

      {/* Expandable */}
      {(expandable || expandableIcon) &&
        ((cell.row as unknown as ExpandedRowProps<TableDatum>).canExpand ? (
          <span
            {...(
              cell.row as unknown as ExpandedRowProps<TableDatum>
            ).getToggleRowExpandedProps({
              className: 'd-flex flex-items-center',
            })}
          >
            {cell.value}
            {expandableIcon &&
              ((row as unknown as ExpandedRowProps<TableDatum>).isExpanded ? (
                <FontAwesomeIcon icon={faChevronUp} />
              ) : (
                <FontAwesomeIcon icon={faChevronDown} />
              ))}
          </span>
        ) : (
          <span>{cell.value}</span>
        ))}

      {/* Badge */}
      {badge && (
        <Badge variant={badge?.variant || ('primary' as BadgeProps['variant'])}>
          {formattedValue}
        </Badge>
      )}

      {/* Single action */}
      {actions &&
        actions.length === 1 &&
        actions?.map((a) => {
          if (customCellComponents && customCellComponents[a.component]) {
            const Component = customCellComponents[
              a.component
            ] as typeof React.Component<CustomActionCellProps>;
            return (
              <Component
                key={a.id}
                cell={cell}
                icon={
                  (actions[0].icon
                    ? getIcon(actions[0].icon)
                    : undefined) as IconProp
                }
                label={
                  parseDataValue(
                    JSON.parse((cell.row.original._original as string) || ''),
                    actions[0].label || '',
                    'string',
                  ) as string
                }
                value={
                  parseDataValue(
                    JSON.parse((cell.row.original._original as string) || ''),
                    actions[0].value || '',
                    'string',
                  ) as string
                }
              />
            );
          }
          return null;
        })}

      {/* Multiple actions */}
      {actions && actions.length > 1 && (
        <ActionsButton>
          {(onDismiss) => (
            <div>
              {actions?.map((a) => {
                if (customCellComponents && customCellComponents[a.component]) {
                  const Component = customCellComponents[
                    a.component
                  ] as typeof React.Component<CustomActionCellProps>;

                  return (
                    <Component
                      cell={cell}
                      key={`${a.label}-${cell.value}`}
                      icon={(a.icon ? getIcon(a.icon) : undefined) as IconProp}
                      onTrigger={onDismiss}
                      label={
                        parseDataValue(
                          JSON.parse(
                            (cell.row.original._original as string) || '',
                          ),
                          a.label || '',
                          'string',
                        ) as string
                      }
                      value={
                        parseDataValue(
                          JSON.parse(
                            (cell.row.original._original as string) || '',
                          ),
                          a.value || '',
                          'string',
                        ) as string
                      }
                    />
                  );
                }
                return null;
              })}
            </div>
          )}
        </ActionsButton>
      )}

      {/* Custom component */}
      {custom &&
        (() => {
          const { component, map, customProps, ...props } = custom;
          if (customCellComponents && customCellComponents[component]) {
            const Component = customCellComponents[
              component
            ] as typeof React.Component<CustomCellProps>;
            return (
              <Component
                cell={cell}
                row={row}
                map={map}
                formattedValue={formattedValue}
                {...props}
                {...customProps}
              />
            );
          }
          return null;
        })()}
    </div>
  );
};

export default CustomCell;
